/**
 * Dropdown Listener Module
 * Registers event listeners for dropdown menu events
 */

import { dropdownToggle } from "./dropdown-toggle";
import { disposeEventScope, getEventScope } from "../../core/event-scope/event-scope";
import { dropdownForm } from "./dropdown-form";

interface DropdownHandlerLike {
    handleMenuButtonClick(event: Event, type: "post" | "comment"): void;
    handleMenuItemClick(event: Event, type?: "post" | "comment"): void;
}

class DropdownListener {
    public menuButtonHandler: ((event: Event) => void) | null;

    public menuItemHandler: ((event: Event) => void) | null;

    public outsideClickHandler: ((event: Event) => void) | null;

    public escapeKeyHandler: ((event: KeyboardEvent) => void) | null;

    public cancelEditHandler: ((event: Event) => void) | null;

    public constructor() {
        this.menuButtonHandler = null;
        this.menuItemHandler = null;
        this.outsideClickHandler = null;
        this.escapeKeyHandler = null;
        this.cancelEditHandler = null;
    }

    public setupGlobalDropdownHandlers(handler: DropdownHandlerLike): void {
        disposeEventScope(document, "dropdown-global");

        const scope = getEventScope(document, "dropdown-global");

        this.menuButtonHandler = (event: Event): void => {
            const target = dropdownForm.getEventTarget(event);
            const postMenuBtn = dropdownForm.getMenuButton(target, "post");
            const commentMenuBtn = dropdownForm.getMenuButton(target, "comment");

            if (postMenuBtn) {
                handler.handleMenuButtonClick(event, "post");
                return;
            }

            if (commentMenuBtn) {
                handler.handleMenuButtonClick(event, "comment");
            }
        };

        this.outsideClickHandler = (event: Event): void => {
            const target = dropdownForm.getEventTarget(event);
            if (
                target &&
                !dropdownForm.getPostMenu(target) &&
                !dropdownForm.getCommentMenu(target) &&
                !dropdownForm.getMenuButton(target, "post") &&
                !dropdownForm.getMenuButton(target, "comment")
            ) {
                dropdownToggle.hideAllDropdownMenus();
            }
        };

        this.menuItemHandler = (event: Event): void => {
            const target = dropdownForm.getEventTarget(event);
            const postMenuItem = dropdownForm.getPostMenuItem(target);
            const commentMenuItem = dropdownForm.getCommentMenuItem(target);

            if (postMenuItem) {
                handler.handleMenuItemClick(event, "post");
                return;
            }

            if (commentMenuItem) {
                handler.handleMenuItemClick(event, "comment");
            }
        };

        this.escapeKeyHandler = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                dropdownToggle.hideAllDropdownMenus();
            }
        };

        scope.on("click", this.menuButtonHandler);
        scope.on("click", this.outsideClickHandler);
        scope.on("click", this.menuItemHandler);
        scope.on("keydown", this.escapeKeyHandler);
    }

    public removeGlobalDropdownHandlers(): void {
        disposeEventScope(document, "dropdown-global");
        this.menuButtonHandler = null;
        this.outsideClickHandler = null;
        this.menuItemHandler = null;
        this.escapeKeyHandler = null;
    }

    public initialize(handler: DropdownHandlerLike): void {
        this.setupGlobalDropdownHandlers(handler);
    }

    public cleanup(): void {
        this.removeGlobalDropdownHandlers();
    }
}

const dropdownListener = new DropdownListener();

export { dropdownListener, DropdownListener };
